/**
 * Colunas e linhas da visão Tabela do setor (PainelTabela, com os filtros
 * por coluna no padrão do Excel). Cada entidade tem o seu conjunto —
 * ambiente, lote, tarefa e PEÇA DESMEMBRADA; quem usa escolhe quais aparecem
 * (Colunas) e a escolha fica nas preferências.
 *
 * Datas saem já como "dd/mm/aaaa": é o texto que a pessoa vê e por onde o
 * filtro de coluna agrupa; `toDate` do PainelTabela lê de volta para ordenar.
 */
import { formatarDataLocal } from "../../../utils/dinheiro";
import { toDate } from "../../../utils/status";
import { agruparPorLote } from "../dados/lotes";
import {
  ROTULO_PRIORIDADE,
  ROTULO_STATUS_AMBIENTE,
  ROTULO_STATUS_PECA,
  ROTULO_STATUS_TAREFA,
  TOM_STATUS_AMBIENTE,
  TOM_STATUS_PECA,
  TOM_STATUS_TAREFA,
  tarefaAtrasada,
} from "../dados/vocabulario";
import { textoDaOrigem, textoDosResponsaveis, textoDosResponsaveisDaPeca } from "./modelo";

// `fixa`: não sai da tabela (sem ela a linha não se identifica).
export const COLUNAS_TABELA = Object.freeze({
  ambiente: [
    { key: "pedido", label: "Pedido", width: "120px", padrao: true, fixa: true },
    { key: "cliente", label: "Cliente", padrao: true },
    { key: "ambiente", label: "Ambiente", padrao: true, fixa: true },
    { key: "lote", label: "Lote", width: "104px", padrao: true },
    { key: "subsetor", label: "Subsetor", padrao: true },
    { key: "posto", label: "Posto", padrao: true },
    { key: "status", label: "Status", width: "200px", padrao: true },
    { key: "prazoPlanilha", label: "Prazo da planilha", tipo: "data", width: "188px" },
    { key: "prazoEfetivo", label: "Prazo que vale", tipo: "data", width: "180px", padrao: true },
    { key: "prevista", label: "Prevista", tipo: "data", width: "132px" },
    { key: "responsavel", label: "Responsável", padrao: true },
    { key: "diasNoSetor", label: "Dias no setor", width: "176px", padrao: true },
    { key: "tarefasAbertas", label: "Tarefas", width: "128px" },
    { key: "pecasFora", label: "Peças fora", width: "140px" },
    { key: "pendencias", label: "Pendências" },
  ],
  lote: [
    { key: "lote", label: "Lote", width: "112px", padrao: true, fixa: true },
    { key: "ambientes", label: "Ambientes", width: "132px", padrao: true },
    { key: "clientes", label: "Clientes", padrao: true },
    { key: "subsetores", label: "Subsetores", padrao: true },
    { key: "status", label: "Status", width: "160px", padrao: true },
    { key: "prazoEfetivo", label: "Prazo mais apertado", tipo: "data", width: "200px", padrao: true },
    { key: "emProducao", label: "Em produção", width: "144px", padrao: true },
    { key: "atrasados", label: "Atrasados", width: "132px", padrao: true },
    { key: "tarefasAbertas", label: "Tarefas", width: "124px" },
    { key: "pecasFora", label: "Peças fora", width: "140px" },
  ],
  tarefa: [
    { key: "codigo", label: "Código", width: "112px", padrao: true, fixa: true },
    { key: "titulo", label: "Tarefa", padrao: true, fixa: true },
    { key: "status", label: "Status", width: "196px", padrao: true },
    { key: "colaborador", label: "Colaborador", padrao: true },
    { key: "subsetor", label: "Subsetor", padrao: true },
    { key: "posto", label: "Posto", padrao: true },
    { key: "ambiente", label: "Ambiente", padrao: true },
    { key: "peca", label: "Peça", width: "180px" },
    { key: "prevista", label: "Prevista", tipo: "data", width: "128px" },
    { key: "limite", label: "Limite", tipo: "data", width: "124px", padrao: true },
    { key: "criadoEm", label: "Criada em", tipo: "data", width: "144px" },
    { key: "criadoPor", label: "Criada por" },
    { key: "comentarios", label: "Comentários", width: "152px" },
  ],
  peca: [
    { key: "codigo", label: "Código", width: "112px", padrao: true, fixa: true },
    { key: "nome", label: "Peça", padrao: true, fixa: true },
    { key: "ambiente", label: "Ambiente de origem", padrao: true },
    { key: "cliente", label: "Cliente", padrao: true },
    { key: "setor", label: "Setor", padrao: true },
    { key: "subsetor", label: "Subsetor", padrao: true },
    { key: "posto", label: "Posto", padrao: true },
    { key: "status", label: "Status", width: "220px", padrao: true },
    { key: "prioridade", label: "Prioridade", width: "168px", padrao: true },
    { key: "prazoEfetivo", label: "Prazo que vale", tipo: "data", width: "180px", padrao: true },
    { key: "origemPrazo", label: "Régua do prazo", width: "180px" },
    { key: "prevista", label: "Prevista", tipo: "data", width: "132px" },
    { key: "responsavel", label: "Responsável", padrao: true },
    { key: "tarefasAbertas", label: "Tarefas", width: "124px" },
    { key: "criadoEm", label: "Desmembrada em", tipo: "data", width: "192px" },
    { key: "criadoPor", label: "Desmembrada por" },
  ],
});

/** Keys visíveis: as salvas (na ordem da definição) ou as padrão; fixas sempre. */
export function colunasVisiveis(entidade, salvas) {
  const definicao = COLUNAS_TABELA[entidade] || [];
  const escolhidas = Array.isArray(salvas) && salvas.length ? new Set(salvas) : null;
  return definicao
    .filter((c) => c.fixa || (escolhidas ? escolhidas.has(c.key) : c.padrao))
    .map((c) => c.key);
}

export function definicaoDasColunas(entidade, keys) {
  const conjunto = new Set(keys);
  return (COLUNAS_TABELA[entidade] || []).filter((c) => conjunto.has(c.key));
}

const data = (valor) => {
  const d = toDate(valor);
  return d ? formatarDataLocal(d) : "";
};

// Instante ISO do app ("2026-09-22T14:03:00-03:00") -> dia local. Não passa
// por `toDate`, que corta o texto no dia e perde o fuso.
const diaDoInstante = (iso) => (iso ? formatarDataLocal(iso) : "");

const nomeDe = (mapa, id) => (id ? mapa.get(id)?.nome || "" : "");

function statusDoAmbiente(d) {
  const rotulo = ROTULO_STATUS_AMBIENTE[d.statusApp] || d.statusApp;
  return {
    status: d.atrasada ? `${rotulo} · atrasado` : rotulo,
    statusVariant: d.atrasada ? "danger" : TOM_STATUS_AMBIENTE[d.statusApp] || "neutral",
  };
}

function linhaDeAmbiente(d, indice) {
  return {
    id: `ambiente:${d.chaveLinha}`,
    pedido: d.pedido,
    cliente: d.cliente,
    ambiente: d.ambiente,
    lote: d.lote,
    subsetor: nomeDe(indice.subsetores, d.subsetorId) || "Fila do setor",
    posto: nomeDe(indice.postos, d.postoId),
    ...statusDoAmbiente(d),
    prazoPlanilha: data(d.prazoPlanilha),
    prazoEfetivo: data(d.prazoEfetivo),
    prevista: data(d.prevista),
    responsavel: textoDosResponsaveis(d, indice),
    diasNoSetor: d.diasNoSetor ?? "",
    tarefasAbertas: (d.tarefas || []).length,
    pecasFora: d.pecasFora || 0,
    pendencias: (d.pendencias || []).map((p) => p.texto).join("; "),
  };
}

function statusDoLote(lote) {
  if (lote.atrasadas > 0) return { status: "Com atraso", statusVariant: "danger" };
  if (lote.emProducao > 0) return { status: "Em produção", statusVariant: "info" };
  return { status: "Aguardando", statusVariant: "neutral" };
}

function distintosEmTexto(valores) {
  return [...new Set(valores.filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR")).join(", ");
}

function linhaDeLote(lote, indice) {
  return {
    id: `lote:${lote.chave}`,
    lote: lote.rotulo,
    ambientes: lote.demandas.length,
    clientes: distintosEmTexto(lote.demandas.map((d) => d.cliente)),
    subsetores: distintosEmTexto(
      lote.demandas.map((d) => nomeDe(indice.subsetores, d.subsetorId) || "Fila do setor")
    ),
    ...statusDoLote(lote),
    prazoEfetivo: data(lote.prazoEfetivo),
    emProducao: lote.emProducao,
    atrasados: lote.atrasadas,
    tarefasAbertas: lote.demandas.reduce((soma, d) => soma + (d.tarefas || []).length, 0),
    pecasFora: lote.pecasFora || 0,
  };
}

function linhaDeTarefa(t, indice, hoje) {
  const atrasada = tarefaAtrasada(t, hoje);
  const rotulo = ROTULO_STATUS_TAREFA[t.status] || t.status;
  return {
    id: `tarefa:${t.id}`,
    codigo: t.codigo,
    titulo: t.titulo,
    status: atrasada ? `${rotulo} · atrasada` : rotulo,
    statusVariant: atrasada ? "danger" : TOM_STATUS_TAREFA[t.status] || "neutral",
    colaborador: nomeDe(indice.colaboradores, t.colaborador_id),
    subsetor: nomeDe(indice.subsetores, t.subsetor_id) || "Fila do setor",
    posto: nomeDe(indice.postos, t.posto_id),
    ambiente: t.ambiente_rotulo || "",
    peca: t.peca_id ? indice.pecas.get(t.peca_id)?.nome || "Peça desmembrada" : "",
    prevista: data(t.data_prevista),
    limite: data(t.data_limite),
    criadoEm: diaDoInstante(t.criado_em),
    criadoPor: t.criado_por || "",
    comentarios: t.comentarios_total || 0,
  };
}

/**
 * A peça já vem combinada (`dados/pecas.js`): `setorNome`, `prazoEfetivo`,
 * `origemPrazo` e `atrasada` estão resolvidos. `origemPrazo` vira a coluna
 * "Régua do prazo" — ADR-0059: dois prazos legítimos do mesmo item precisam
 * dizer na tela qual é qual.
 */
function linhaDePeca(p, indice) {
  const rotulo = ROTULO_STATUS_PECA[p.status] || p.status;
  return {
    id: `peca:${p.id}`,
    codigo: p.codigo,
    nome: p.nome,
    ambiente: p.ambienteRotulo || "",
    cliente: p.cliente || "",
    setor: p.setorNome || "",
    subsetor: nomeDe(indice.subsetores, p.subsetorId) || "Fila do setor",
    posto: nomeDe(indice.postos, p.postoId),
    status: p.atrasada ? `${rotulo} · atrasada` : rotulo,
    statusVariant: p.atrasada ? "danger" : TOM_STATUS_PECA[p.status] || "neutral",
    prioridade: ROTULO_PRIORIDADE[p.prioridade] || "",
    prazoEfetivo: data(p.prazoEfetivo),
    origemPrazo: textoDaOrigem(p.origemPrazo),
    prevista: data(p.prevista),
    responsavel: textoDosResponsaveisDaPeca(p, indice),
    tarefasAbertas: (p.tarefas || []).length,
    criadoEm: diaDoInstante(p.criado_em),
    criadoPor: p.criado_por || "",
  };
}

/**
 * Linhas da tabela + o item de origem por id (para abrir o detalhe ao
 * clicar): {linhas, itemPorId: Map<id, {tipo, item}>}.
 */
export function montarLinhas(entidade, { demandas = [], tarefas = [], pecas = [], indice, hoje = new Date() }) {
  const itemPorId = new Map();
  const montar = (lista, tipo, construir) =>
    lista.map((item) => {
      const linha = construir(item);
      itemPorId.set(linha.id, { tipo, item });
      return linha;
    });

  let linhas;
  if (entidade === "lote") {
    linhas = montar(agruparPorLote(demandas, { pecas }), "lote", (lote) => linhaDeLote(lote, indice));
  } else if (entidade === "tarefa") {
    linhas = montar(tarefas, "tarefa", (t) => linhaDeTarefa(t, indice, hoje));
  } else if (entidade === "peca") {
    linhas = montar(pecas, "peca", (p) => linhaDePeca(p, indice));
  } else {
    linhas = montar(demandas, "ambiente", (d) => linhaDeAmbiente(d, indice));
  }
  return { linhas, itemPorId };
}

const TITULOS = {
  lote: "Lotes",
  tarefa: "Tarefas",
  peca: "Peças desmembradas",
  ambiente: "Ambientes",
};

/** "Ambientes · Corte" — `nomeSetor` vem do catálogo (o gestor renomeia). */
export function tituloDaTabela(entidade, nomeSetor) {
  const titulo = TITULOS[entidade] || TITULOS.ambiente;
  return nomeSetor ? `${titulo} · ${nomeSetor}` : titulo;
}

/** Rótulo da contagem do rodapé, por entidade. */
export function labelDaContagem(entidade) {
  if (entidade === "tarefa") return "Total de tarefas";
  if (entidade === "peca") return "Total de peças";
  if (entidade === "lote") return "Total de lotes";
  return "Total de ambientes";
}
